import React, { useContext, useEffect, useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, Tooltip, TextField, Stack,
  TableRow, Paper, IconButton, Tabs, Tab, Divider, TablePagination, Box, Typography, Modal, Button
} from "@mui/material";
import VisibilityIcon from '@mui/icons-material/Visibility';
import { styled } from '@mui/system';
import { useMediaQuery } from '@mui/material';
import { ContextSession } from "../../services/ContextSession";
import MarkunreadIcon from '@mui/icons-material/Markunread';
import DraftsIcon from '@mui/icons-material/Drafts';
import Lottie from 'react-lottie-player';
import logoInjupemp from '../img/injupemplogogobierno.png';
import inboxLottie from '../img/inboxLottie.json';
import CalendarTodayIcon from '@mui/icons-material/CalendarMonth';
import AccountCircleIcon from '@mui/icons-material/SwitchAccount';
import { createTheme, ThemeProvider, useTheme } from '@mui/material/styles';
import { outlinedInputClasses } from '@mui/material/OutlinedInput';
import CreateIcon from '@mui/icons-material/Create'; // Ícono de lápiz para redactar
import { Send as SendIcon, Subject as SubjectIcon, Person as PersonIcon } from '@mui/icons-material';
import MailIcon from '@mui/icons-material/Mail';
import AllInboxIcon from '@mui/icons-material/Inbox';
import { Divide } from 'lucide-react';
import Chip from '@mui/material/Chip';

const customTheme = (outerTheme) =>
  createTheme({
    palette: {
      mode: outerTheme.palette.mode,
    },
    components: {
      MuiTextField: {
        styleOverrides: {
          root: {
            '--TextField-brandBorderColor': '#E0E3E7',
            '--TextField-brandBorderHoverColor': '#B2BAC2',
            '--TextField-brandBorderFocusedColor': '#6F7E8C',
            '& label.Mui-focused': {
              color: 'var(--TextField-brandBorderFocusedColor)',
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          notchedOutline: {
            borderColor: 'var(--TextField-brandBorderColor)',
          },
          root: {
            [`&:hover .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: 'var(--TextField-brandBorderHoverColor)',
            },
            [`&.Mui-focused .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: 'var(--TextField-brandBorderFocusedColor)',
            },
          },
        },
      },
      MuiFilledInput: {
        styleOverrides: {
          root: {
            '&::before, &::after': {
              borderBottom: '2px solid var(--TextField-brandBorderColor)',
            },
            '&:hover:not(.Mui-disabled, .Mui-error):before': {
              borderBottom: '2px solid var(--TextField-brandBorderHoverColor)',
            },
            '&.Mui-focused:after': {
              borderBottom: '2px solid var(--TextField-brandBorderFocusedColor)',
            },
          },
        },
      },
      MuiInput: {
        styleOverrides: {
          root: {
            '&::before': {
              borderBottom: '2px solid var(--TextField-brandBorderColor)',
            },
            '&:hover:not(.Mui-disabled, .Mui-error):before': {
              borderBottom: '2px solid var(--TextField-brandBorderHoverColor)',
            },
            '&.Mui-focused:after': {
              borderBottom: '2px solid var(--TextField-brandBorderFocusedColor)',
            },
          },
        },
      },
    },
  });

// Estilo personalizado para el modal
const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '80%',
  maxWidth: 500,
  bgcolor: 'background.paper',
  borderRadius: '12px',
  boxShadow: 24,
  p: 4,
};

// Estilos personalizados para la tabla
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  backgroundColor: 'var(--color-brand-primary)',
  color: 'var(--color-white)',
  padding: '16px',
}));

const StyledTableRow = styled(TableRow)(({ theme, read }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: '#f9f9f9',
  },
  '&:hover': {
    backgroundColor: '#e3f2fd',
    cursor: 'pointer',
  },
}));

const Inbox = () => {
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [open, setOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5); // Inicialmente mostrar 5 solicitudes por página
  const isMobile = useMediaQuery('(max-width:820px)');
  const sessi = useContext(ContextSession);
  const [openCompose, setOpenCompose] = useState(false); // Estado para abrir/cerrar el modal
  const [message, setMessage] = useState({ sender: '', subject: '', content: '' }); // Datos del mensaje

  const handleOpenCompose = () => setOpenCompose(true);
  // Función para cerrar el modal y resetear los errores
  const handleCloseCompose = () => {
    // Resetear los errores cuando el modal se cierre
    setSubjectError('');
    setContentError('');
    setOpenCompose(false); // Cerrar el modal
  };

  const [showText, setShowText] = useState(true);

  useEffect(() => {
    // Después de 5 segundos, ocultamos el texto
    const timer = setTimeout(() => {
      setShowText(false);
    }, 5000);

    // Limpiamos el temporizador al desmontarse el componente
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const fetchMessages = async () => {
      const formData = new FormData();
      //formData.append('usuarioId', '33');
      formData.append('usuarioId', sessi.sessionData.id);

      try {
        const response = await fetch(`${sessi.ip}/api/buzonPart/`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${sessi.sessionData.token}`,
          },
          body: formData,
        });

        if (response.ok) {
          const data = await response.json();
          //console.log("Data API:", data);
          const messagesWithDate = data.map((message) => {
            const fecha = new Date(message.Fecha_envio);
            const opcionesFecha = {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
              hour12: true
            };
            const fechaFormateada = fecha.toLocaleString('es-ES', opcionesFecha);

            return {
              id: message.Buzon_id,
              sender: message.remitente,
              date: fechaFormateada,
              parsedDate: fecha, // Fecha en formato Date para ordenar
              subject: message.Asunto,
              content: message.Contenido,
              read: message.Estado_leido === 1
            };
          });

          setMessages(messagesWithDate);
        } else {
          setMessages([]);
        }
      } catch (error) {
        console.error("Error fetching messages:", error);
        setMessages([]);
      }
    };

    fetchMessages();
  }, [sessi.sessionData.id, sessi.sessionData.token]);

  const generateRandomDate = () => {
    const start = new Date(2023, 0, 1);
    const end = new Date();
    const randomDate = new Date(start.getTime() + Math.random() * (end.getTime() - start.getTime()));
    return randomDate.toISOString().split('T')[0];
  };

  // Función para manejar los cambios en los campos de entrada
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setMessage((prevMessage) => ({
      ...prevMessage,
      [name]: value,
    }));
  };

  const [subjectError, setSubjectError] = useState('');
  const [contentError, setContentError] = useState('');

  // Modificar la función de enviar mensaje
  const handleSendMessage = () => {
    // Resetear los errores
    setSubjectError('');
    setContentError('');

    let isValid = true;

    // Validar el campo Asunto
    if (!message.subject) {
      setSubjectError('El asunto no puede estar vacío.');
      isValid = false;
    }

    // Validar el campo Contenido
    if (!message.content) {
      setContentError('El contenido no puede estar vacío.');
      isValid = false;
    }

    // Si ambos campos son válidos, llamar a sendMessage
    if (isValid) {
      sendMessage();
    }
  };

  // Función para enviar el mensaje
  const sendMessage = async () => {
    const formData = new FormData();
    formData.append('remitente', message.sender);
    formData.append('asunto', message.subject);
    formData.append('contenido', message.content);

    try {
      const response = await fetch(`${sessi.ip}/api/sendMessage/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessi.sessionData.token}`,
        },
        body: formData,
      });

      if (response.ok) {
        alert("Mensaje enviado con éxito");
        setMessage({ sender: '', subject: '', content: '' }); // Limpiar formulario
        setOpenCompose(false); // Cerrar el modal
      } else {
        alert("Error al enviar el mensaje");
      }
    } catch (error) {
      console.error("Error enviando el mensaje:", error);
      alert("Error al enviar el mensaje");
    }
  };

  const handleOpen = async (message) => {
    setSelectedMessage(message);
    setOpen(true);

    // Si el mensaje no está leído, enviar la solicitud para actualizar el estado a leído
    if (!message.read) {
      const formData = new FormData();
      formData.append('Estado_leido', 1);
      formData.append('Fecha_lectura', new Date().toISOString()); // Formato '2024-09-24T19:58:12'

      try {
        const response = await fetch(`${sessi.ip}/api/buzon/${message.id}/`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${sessi.sessionData.token}`,
          },
          body: formData,
        });

        if (response.ok) {
          // Actualizar el estado del mensaje localmente
          const updatedMessages = messages.map((msg) =>
            msg.id === message.id ? { ...msg, read: true } : msg
          );
          setMessages(updatedMessages);

          // Disparar el evento para actualizar el badge en Menu.jsx
          window.dispatchEvent(new Event('messageRead'));
        } else {
          console.error('Error al actualizar el estado del mensaje');
        }
      } catch (error) {
        console.error('Error al intentar actualizar el mensaje:', error);
      }
    }
  };

  const handleClose = () => setOpen(false);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Resetear la página cuando cambia la cantidad de filas por página
  };

  // Lógica para ordenar mensajes por estado y fecha antes de paginar
  const sortedMessages = [...messages].sort((a, b) => {
    if (a.read === b.read) {
      // Usar parsedDate para ordenar por fecha descendente
      return b.parsedDate - a.parsedDate;
    } else {
      // Si no tienen el mismo estado, colocar "No Leído" antes de "Leído"
      return a.read - b.read;
    }
  });

  const filteredMessages = tabValue === 0 ? sortedMessages : sortedMessages.filter((message) => !message.read);
  const paginatedMessages = filteredMessages.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const outerTheme = useTheme();

  return (
    <div>
      <Box display="flex" alignItems="center" flexDirection="column" marginTop={!isMobile ? 5 : 0}>
        <Lottie loop animationData={inboxLottie} play style={{ width: !isMobile ? '5%' : '15%', height: 'auto' }} />
        <Typography variant="h4" align="center" sx={{ fontWeight: 'bold' }}>
          Bandeja de Entrada
        </Typography>
      </Box>

      {/*<Stack direction="column" alignItems="center" spacing={2} marginTop={1}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleOpenCompose}
          endIcon={<CreateIcon />}
          sx={{
            '&:hover': {
              color: 'var(--color-white)', // Cambiar el color del texto a blanco al hacer hover
              '& .MuiButton-startIcon': {
                color: 'var(--color-white)', // Cambiar el color del ícono a blanco al hacer hover
              }
            }
          }}
        >
          Redactar Mensaje
        </Button>
      </Stack>*/}

      <Tabs value={tabValue} onChange={handleTabChange} centered style={{ marginTop: 10 }}>
        <Tab label="Todo" icon={<AllInboxIcon />} />
        <Tab label="No leídos" icon={<MailIcon />} />
        {/*<Tab label="Enviados" icon={<SendIcon />} />*/}
      </Tabs>

      <div>
        {/* Botón flotante de redacción */}
        {/*<Tooltip title="Redactar Mensaje" arrow placement='left'>
          <IconButton
            color="primary"
            onClick={handleOpenCompose}
            style={{
              position: 'fixed',
              bottom: '20px',
              right: isMobile ? 'auto' : '20px',
              left: isMobile ? '20px' : 'auto',
              backgroundColor: 'var(--color-brand-primary)',
              padding: '10px',
              borderRadius: showText ? '18px' : '50%', // Cambia la forma dependiendo del estado
              zIndex: 9999,
              transition: 'border-radius 0.3s ease', // Añadimos una transición para suavizar el cambio
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {showText && <span style={{ marginRight: '8px', color: 'var(--color-white)', fontSize: 18 }}>Redactar Mensaje</span>}
              <CreateIcon style={{ color: 'var(--color-white)' }} />
            </div>
          </IconButton>
        </Tooltip>
        */}

        {/* Modal para redactar mensaje */}
        <Modal
          open={openCompose}
          onClose={handleCloseCompose}
          aria-labelledby="compose-message-title"
          aria-describedby="compose-message-description"
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '90%',
              maxWidth: 600,
              bgcolor: 'background.paper',
              padding: 3,
              borderRadius: 3,
              boxShadow: 24,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Redactar Mensaje
              </Typography>
            </Box>

            <TextField
              label="Destinatario"
              name="sender"
              value={sessi.sessionData.nombre}
              fullWidth
              margin="normal"
              InputProps={{
                startAdornment: <PersonIcon sx={{ color: 'var(--color-gray-500)', marginRight: 1 }} />,
                readOnly: true, // Esto hace que el campo sea solo lectura
              }}
            />

            <TextField
              label="Asunto"
              name="subject"
              value={message.subject}
              onChange={handleInputChange}
              fullWidth
              margin="normal"
              placeholder="Ingrese el asunto aquí"
              InputProps={{
                startAdornment: <SubjectIcon sx={{ color: 'var(--color-gray-500)', marginRight: 1 }} />,
              }}
              error={!!subjectError} // Mostrar error si el estado de error de Asunto tiene un valor
              helperText={subjectError} // Mostrar el mensaje de error
            />

            <TextField
              label="Contenido"
              name="content"
              value={message.content}
              onChange={handleInputChange}
              multiline
              rows={4}
              fullWidth
              margin="normal"
              placeholder="Ingrese contenido aquí"
              error={!!contentError} // Mostrar error si el estado de error de Contenido tiene un valor
              helperText={contentError} // Mostrar el mensaje de error
            />

            <Divider sx={{ marginY: 2, width: '100%' }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
              <Button onClick={handleCloseCompose} variant="outlined" color="error" fullWidth sx={{ marginRight: 1 }}>
                Cancelar
              </Button>
              <Button
                onClick={handleSendMessage} // Llamar a la función de enviar mensaje
                variant="contained"
                color="primary"
                fullWidth
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  '&:hover': {
                    color: 'var(--color-white)', // Cambiar el color del texto a blanco al hacer hover
                    '& .MuiButton-startIcon': {
                      color: 'var(--color-white)', // Cambiar el color del ícono a blanco al hacer hover
                    },
                  },
                }}
              >
                <SendIcon sx={{ marginRight: 1 }} />
                Enviar
              </Button>
            </Box>
          </Box>
        </Modal>
      </div>

      <TableContainer component={Paper} sx={{ boxShadow: '0px 12px 24px rgba(0, 0, 0, 0.1)', borderRadius: 3, marginTop: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableCell style={{ fontSize: isMobile ? '12px' : '14px' }}>Remitente</StyledTableCell>
              <StyledTableCell style={{ fontSize: isMobile ? '12px' : '14px' }}>Fecha</StyledTableCell>
              <StyledTableCell style={{ fontSize: isMobile ? '12px' : '14px' }}>Asunto</StyledTableCell>
              {!isMobile && (
                <StyledTableCell style={{ fontSize: isMobile ? '12px' : '14px' }}>Estado</StyledTableCell>
              )}
              {/*<StyledTableCell style={{ fontSize: isMobile ? '12px' : '14px' }}>Acción</StyledTableCell>*/}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedMessages.length === 0 ? (
              <StyledTableRow>
                <TableCell colSpan={isMobile ? 4 : 5} align="center">No se han recibido mensajes.</TableCell>
              </StyledTableRow>
            ) : (
              paginatedMessages.map((message) => (
                <StyledTableRow
                  key={message.id}
                  read={message.read}
                  onClick={() => handleOpen(message)}
                >
                  <TableCell sx={{ fontWeight: message.read ? 'normal' : 'bold', fontSize: isMobile ? '12px' : '14px' }}>
                    {message.sender}
                  </TableCell>
                  <TableCell sx={{ fontWeight: message.read ? 'normal' : 'bold', fontSize: isMobile ? '12px' : '14px' }}>
                    {message.date}
                  </TableCell>
                  <TableCell sx={{ fontWeight: message.read ? 'normal' : 'bold', fontSize: isMobile ? '12px' : '14px' }}>
                    {message.subject}
                  </TableCell>
                  {!isMobile && (
                    <TableCell sx={{ fontWeight: message.read ? 'normal' : 'bold', fontSize: isMobile ? '12px' : '14px' }}>
                      {message.read ? (
                        <>
                          <DraftsIcon sx={{ color: 'var(--color-gray-500)', verticalAlign: 'middle', marginRight: '8px' }} />
                          <span style={{ verticalAlign: 'middle' }}>Leído</span>
                        </>
                      ) : (
                        <>
                          <MarkunreadIcon sx={{ color: 'var(--color-gray-500)', verticalAlign: 'middle', marginRight: '8px' }} />
                          <span style={{ verticalAlign: 'middle' }}>No leído</span>
                        </>
                      )}
                    </TableCell>
                  )}
                  {/*<TableCell>
                    <Tooltip title="Ver Mensaje" arrow placement='right'>
                      <IconButton
                        color={message.read ? "default" : "primary"}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpen(message);
                        }}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>*/}
                </StyledTableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={filteredMessages.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage={isMobile ? "Filas:" : "Filas por página"}
        rowsPerPageOptions={isMobile ? [3, 5, 10] : [5, 10, 20]}
      />

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-message-title"
        aria-describedby="modal-message-description"
      >
        <Box sx={modalStyle}>
          {selectedMessage && (
            <>
              {/* Imagen ajustada para móviles */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                <img
                  src={logoInjupemp}
                  alt="Logo Injupemp"
                  style={{ maxWidth: isMobile ? '90px' : '150px' }}
                />
              </Box>
              <Divider />
              <Typography
                id="modal-message-title"
                variant="h6"
                component="h2"
                sx={{ fontWeight: 'bold', mb: isMobile ? 1 : 2, fontSize: isMobile ? 15 : 20, marginTop: 1 }} // Ajustar tamaño
              >
                Asunto: {selectedMessage.subject}
              </Typography>

              {/* Contenedor ajustado para "De" y "Fecha" */}
              <Box sx={{
                display: isMobile ? 'block' : 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2
              }}>
                {/* Ícono de usuario a la izquierda de "De" */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: isMobile ? 1 : 0 }}>
                  <IconButton size="small" edge="start" sx={{ mr: 1 }}>
                    <AccountCircleIcon />
                  </IconButton>
                  <Typography
                    id="modal-message-sender"
                    color="textSecondary"
                    sx={{ fontSize: isMobile ? 13 : 15 }} // Ajustar tamaño
                  >
                    De: {selectedMessage.sender}
                  </Typography>
                </Box>

                {/* Ícono de calendario a la izquierda de "Fecha" */}
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <IconButton size="small" edge="start" sx={{ mr: 1 }}>
                    <CalendarTodayIcon />
                  </IconButton>
                  <Typography
                    id="modal-message-date"
                    color="textSecondary"
                    sx={{ fontSize: isMobile ? 13 : 15 }} // Ajustar tamaño
                  >
                    Fecha: {selectedMessage.date}
                  </Typography>
                </Box>
              </Box>

              <Divider />

              <ThemeProvider theme={customTheme(outerTheme)}>
                <TextField
                  id="modal-message-description"
                  variant="filled"
                  label="Mensaje:"
                  multiline
                  fullWidth
                  value={selectedMessage.content}
                  InputProps={{
                    readOnly: true,
                    sx: {
                      textAlign: 'justify', // Justificar el texto
                      '& .MuiInputBase-input': {
                        textAlign: 'justify', // Justificar dentro del campo de entrada
                      },
                    },
                  }}
                  sx={{ mt: isMobile ? 2 : 3 }}
                />
              </ThemeProvider>

              {/* Botón Cerrar */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: isMobile ? 2 : 3 }}>
                <Button
                  onClick={handleClose}
                  variant="contained"
                  sx={{
                    backgroundColor: 'var(--color-brand-primary)',
                    color: 'var(--color-white)',
                    ':hover': { backgroundColor: 'var(--color-brand-secondary)' },
                    fontSize: isMobile ? '0.8rem' : '1rem' // Ajustar tamaño de fuente en móvil
                  }}
                >
                  Cerrar
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Modal>


    </div>
  );
};

export default Inbox;


